<template>
  <article class="ai-visibility-recommendation-card" :class="`ai-visibility-recommendation-card--${item.impact}`">
    <impact-badge :impact="item.impact" />
    <h3>{{ item.title }}</h3>
    <p>{{ item.description }}</p>
    <router-link
      v-if="sourceOpportunityTo"
      class="ai-visibility-recommendation-card__action"
      :to="sourceOpportunityTo"
    >
      {{ actionLabel }}
    </router-link>
    <button
      v-else-if="actionType === 'source_opportunity'"
      type="button"
      class="ai-visibility-recommendation-card__action"
      @click="$emit('navigate-tab', 'AiVisibilityCitations')"
    >
      {{ actionLabel }}
    </button>
    <button
      v-else-if="newPopReportTo"
      type="button"
      class="ai-visibility-recommendation-card__action"
      :disabled="navigatingToNewPage"
      @click="handleNewPopReport"
    >
      {{ actionLabel }}
    </button>
  </article>
</template>

<script>
import ImpactBadge from '../../../ui/display/ImpactBadge.vue'

export default {
  name: 'RecommendationCard',
  components: {
    ImpactBadge,
  },
  data() {
    return {
      navigatingToNewPage: false,
    }
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    siteId: {
      type: [Number, String],
      required: true,
    },
    trackerId: {
      type: [Number, String],
      required: true,
    },
    citationsActionTo: {
      type: [Object, String],
      default: null,
    },
  },
  computed: {
    popAction() {
      return this.item.popAction || null
    },
    actionType() {
      return this.popAction?.type || ''
    },
    actionLabel() {
      if (this.popAction?.label) return this.popAction.label
      if (this.actionType === 'source_opportunity') return 'View sources'
      if (this.actionType === 'new_pop_report') return 'Create POP report'
      return ''
    },
    sourceOpportunityTo() {
      if (this.actionType === 'source_opportunity') {
        return this.citationsActionTo || {
          name: 'AiVisibilityCitations',
          params: {
            siteId: String(this.siteId),
            trackerId: String(this.trackerId),
          },
        }
      }

      return null
    },
    newPopReportTo() {
      if (this.actionType === 'new_pop_report') {
        const keyword = this.popAction?.prefill?.keyword
        const externalData = keyword
          ? {keyword}
          : undefined

        return {
          name: 'NewPage',
          params: {
            siteId: String(this.siteId),
            ...(externalData ? {externalData} : {}),
          },
        }
      }

      return null
    },
  },
  methods: {
    ensureSiteLoaded() {
      const siteId = Number(this.siteId)

      if (this.$store.getters.siteById(siteId)) {
        return Promise.resolve(true)
      }

      return this.$store.dispatch('loadSite', siteId)
        .then((response) => {
          if (response.data?.status === 'SUCCESS' && response.data?.site) {
            this.$store.commit('setSite', response.data.site)
            return true
          }

          return false
        })
    },
    handleNewPopReport() {
      if (this.navigatingToNewPage) {
        return Promise.resolve()
      }

      this.navigatingToNewPage = true
      this.$store.commit('showLoading')

      return this.ensureSiteLoaded()
        .then((siteLoaded) => {
          if (!siteLoaded) {
            this.$notify({
              group: 'info',
              type: 'error',
              text: 'Could not load project details.',
            })
            return null
          }

          return this.$router.push(this.newPopReportTo)
        })
        .catch(() => {
          this.$notify({
            group: 'info',
            type: 'error',
            text: 'Could not load project details.',
          })
        })
        .finally(() => {
          this.navigatingToNewPage = false
          this.$store.commit('hideLoading')
        })
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-recommendation-card {
  display: flex;
  max-width: 285px;
  min-width: 220px;
  min-height: 190px;
  flex: 1 1 0;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid var(--v-gray13Color-base);
  border-top: 4px solid var(--v-yellow11Color-base);
  border-radius: 8px;
  background: var(--v-cardBg-base);
  padding: 18px;
}

.ai-visibility-recommendation-card--high {
  border-top-color: var(--v-red11Color-base);
}

.ai-visibility-recommendation-card--medium {
  border-top-color: var(--v-profileAnchorColor-base);
}

.ai-visibility-recommendation-card--low {
  border-top-color: var(--v-yellow11Color-base);
}

.ai-visibility-recommendation-card h3 {
  color: var(--v-mainColor-base);
  font-size: 17px;
  font-weight: 800;
  line-height: 1.3;
  margin: 14px 0 8px;
}

.ai-visibility-recommendation-card p {
  color: var(--v-mainColor-base);
  flex: 1 1 auto;
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}

.ai-visibility-recommendation-card__action {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--v-profileAnchorColor-base);
  border-radius: 6px;
  background: transparent;
  color: var(--v-profileAnchorColor-base);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  margin-top: 16px;
  padding: 7px 12px;
  text-decoration: none;
  margin-right: auto;
  margin-left: auto;
}

.ai-visibility-recommendation-card__action:hover {
  background: var(--v-gray12Color-base);
}

.ai-visibility-recommendation-card__action:disabled {
  cursor: wait;
  opacity: 0.7;
}

.ai-visibility-recommendation-card__action:focus-visible {
  outline: 2px solid var(--v-lightYellowColor-base);
  outline-offset: 2px;
}
</style>
